import dayjs, { ConfigType, Dayjs, OpUnitType } from 'dayjs';

import { DateTimeFormats, END_OF_TIME_MS } from '$app-web/constants';

class DateTime {
    instance: Dayjs;

    constructor(dateTime?: dayjs.ConfigType, parseFormat?: dayjs.OptionType) {
        if (dateTime instanceof DateTime) {
            this.instance = dayjs(dateTime.instance, parseFormat);
        } else {
            this.instance = dayjs(dateTime as ConfigType, typeof dateTime === 'number' ? undefined : parseFormat);
        }
        return this;
    }

    isValid(): boolean {
        return this.instance != null && this.instance.isValid();
    }

    isBefore(dateTime: DateTime): boolean {
        return this.instance.isBefore(dateTime.instance);
    }

    isAfter(dateTime: DateTime): boolean {
        return this.instance.isAfter(dateTime.instance);
    }

    isBeforeToday(): boolean {
        return this.instance.isBefore(new DateTime(undefined).instance, 'day');
    }

    isSame(dateTime: DateTime, unit?: OpUnitType): boolean {
        return this.instance.isSame(dateTime.instance, unit);
    }

    isSameDay(dateTime: DateTime): boolean {
        return this.instance.isSame(dateTime.instance, 'day');
    }

    isSameWeek(dateTime: DateTime): boolean {
        return this.instance.isSame(dateTime.instance, 'week');
    }

    isSameMonth(dateTime: DateTime): boolean {
        return this.instance.isSame(dateTime.instance, 'month');
    }

    isSameYear(dateTime: DateTime): boolean {
        return this.instance.isSame(dateTime.instance, 'year');
    }

    diffMillisecond(dateTime: DateTime, floatingPointResult = false): number {
        return this.instance.diff(dateTime.instance, 'millisecond', floatingPointResult);
    }

    diffSecond(dateTime: DateTime, floatingPointResult = false): number {
        return this.instance.diff(dateTime.instance, 'second', floatingPointResult);
    }

    diffMinute(dateTime: DateTime, floatingPointResult = false): number {
        return this.instance.diff(dateTime.instance, 'minute', floatingPointResult);
    }

    diffHour(dateTime: DateTime, floatingPointResult = false): number {
        return this.instance.diff(dateTime.instance, 'hour', floatingPointResult);
    }

    diffDay(dateTime: DateTime, floatingPointResult = false): number {
        return this.instance.diff(dateTime.instance, 'day', floatingPointResult);
    }

    diffWeek(dateTime: DateTime, floatingPointResult = false): number {
        return this.instance.diff(dateTime.instance, 'week', floatingPointResult);
    }

    diffMonth(dateTime: DateTime, floatingPointResult = false): number {
        return this.instance.diff(dateTime.instance, 'month', floatingPointResult);
    }

    diffYear(dateTime: DateTime, floatingPointResult = false): number {
        return this.instance.diff(dateTime.instance, 'year', floatingPointResult);
    }

    getFullYear(): number {
        return this.instance.year();
    }

    getMonth(): number {
        return this.instance.month();
    }

    toMillis(): number {
        return this.instance.valueOf();
    }

    toISOString(): string {
        return this.instance.toISOString();
    }

    // (formatStr): one of the `CreateDateTime.FORMATS`.
    // return: formatted `string`
    format(formatStr: string, { formatUtc }: { formatUtc?: boolean } = {}): string {
        const time = this.instance.locale(dayjs.locale());
        return (formatUtc ? dayjs.utc(time.valueOf()) : time).format(formatStr);
    }

    startOfDay(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).startOf('day');
        return new DateTime(dayjsInstance);
    }

    startOfMinute(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).startOf('minute');
        return new DateTime(dayjsInstance);
    }

    startOfHour(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).startOf('hour');
        return new DateTime(dayjsInstance);
    }

    startOfWeek(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).startOf('week');
        return new DateTime(dayjsInstance);
    }

    startOfMonth(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).startOf('month');
        return new DateTime(dayjsInstance);
    }

    startOfYear(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).startOf('year');
        return new DateTime(dayjsInstance);
    }

    endOfYear(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).endOf('year');
        return new DateTime(dayjsInstance);
    }

    endOfDay(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).endOf('day');
        return new DateTime(dayjsInstance);
    }

    endOfWeek(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).endOf('week');
        return new DateTime(dayjsInstance);
    }

    endOfMonth(): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).endOf('month');
        return new DateTime(dayjsInstance);
    }

    addMonth(monthCount: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).add(monthCount, 'month');
        return new DateTime(dayjsInstance);
    }

    addYear(yearCount: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).add(yearCount, 'year');
        return new DateTime(dayjsInstance);
    }

    addWeek(weekCount: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).add(weekCount, 'week');
        return new DateTime(dayjsInstance);
    }

    addDay(dayCount: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).add(dayCount, 'day');
        return new DateTime(dayjsInstance);
    }

    addHour(hour: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).add(hour, 'hour');
        return new DateTime(dayjsInstance);
    }

    subtractDay(dayCount: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).subtract(dayCount, 'day');
        return new DateTime(dayjsInstance);
    }

    subtractMonth(monthCount: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).subtract(monthCount, 'month');
        return new DateTime(dayjsInstance);
    }

    subtractWeek(weekCount: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).subtract(weekCount, 'week');
        return new DateTime(dayjsInstance);
    }

    subtractHour(hour: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).subtract(hour, 'hour');
        return new DateTime(dayjsInstance);
    }

    getWeekDay(): number {
        return this.instance.day();
    }

    getDayOfMonth(): number {
        return this.instance.date();
    }

    getHour(): number {
        return this.instance.hour();
    }

    getMinute(): number {
        return this.instance.minute();
    }

    setSecond(s: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).second(s);
        return new DateTime(dayjsInstance);
    }

    setMinute(m: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).minute(m);
        return new DateTime(dayjsInstance);
    }

    setHour(h: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).hour(h);
        return new DateTime(dayjsInstance);
    }

    setMonth(m: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).set('month', m);
        return new DateTime(dayjsInstance);
    }

    setYear(y: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).set('year', y);
        return new DateTime(dayjsInstance);
    }

    setMonthDay(d: number): DateTime {
        const dayjsInstance = this.instance.locale(dayjs.locale()).set('date', d);
        return new DateTime(dayjsInstance);
    }

    getSecond(): number {
        return this.instance.second();
    }

    getAllWeekdaysOfDate(): Array<DateTime> {
        const dayjsInstance = this.startOfWeek();
        return Array.from({ length: 7 }, (_, index) => dayjsInstance.addDay(index));
    }

    fromNow(withoutSuffix?: boolean): string {
        return this.instance.fromNow(withoutSuffix);
    }

    from(compared: DateTime, withoutSuffix?: boolean): string {
        return this.instance.from(compared.instance, withoutSuffix);
    }

    toNow(withoutSuffix?: boolean): string {
        return this.instance.toNow(withoutSuffix);
    }

    to(compared: DateTime, withoutSuffix?: boolean): string {
        return this.instance.to(compared.instance, withoutSuffix);
    }
}

export function getWeekDaysShortName() {
    const weekDay = new DateTime().startOfWeek().getWeekDay();
    const daysOfWeekNumber: Array<number> = [];

    Array.from({ length: 7 }, (_, index) => daysOfWeekNumber.push(index), weekDay);
    Array.from({ length: weekDay }, (_, index) => daysOfWeekNumber.push(index));

    return daysOfWeekNumber.map((i) => new DateTime().instance.day(i).format('ddd'));
}

export function humanizeDuration(totalDurationInSecond: number) {
    // TODO: Replace it with dayjs.duration
    const hour = Math.floor(totalDurationInSecond / 3600);
    const minute = Math.floor((totalDurationInSecond - hour * 3600) / 60);
    const second = Math.round(totalDurationInSecond % 60);

    const hourWithPrefix = (hour < 10 ? '0' : '') + hour;

    return `${hour ? `${hourWithPrefix}:` : ''}${minute < 10 ? '0' : ''}${minute}:${second < 10 ? '0' : ''}${second}`;
}

export function getCurrentTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function parseTimezone(dateTime: unknown, timezoneName: string): DateTime {
    if (dateTime instanceof DateTime) {
        return new DateTime(dayjs.tz(dateTime.format(DateTimeFormats.DateTime), timezoneName));
    }
    return new DateTime(dayjs.tz(dateTime as ConfigType, timezoneName));
}

export function getTimezoneOffset(timezone: string): string {
    const offsetInMinutes = parseTimezone(new DateTime(), timezone).instance.utcOffset();
    const offsetInHours = offsetInMinutes / 60;
    const isPositive = offsetInHours > 0;

    const hours = isPositive ? Math.floor(offsetInHours) : Math.ceil(offsetInHours);
    const minutes = offsetInMinutes % 60;
    return `${isPositive ? '+' : ''}${hours}${minutes > 0 ? `:${minutes}` : ''}`;
}

export function isEndOfTime(dateTime: Dayjs): boolean {
    return appDateTime(dateTime).getFullYear() === appDateTime(END_OF_TIME_MS).getFullYear();
}

// Function overload to maintain the same interface as the original JS implementation
export function appDateTime(dateTime?: dayjs.ConfigType, parseFormat?: dayjs.OptionType): DateTime {
    return new DateTime(dateTime, parseFormat);
}
